import React, { FormEvent } from 'react';
import { Button } from 'src/components/Button';
import { InputFile } from 'src/components/InputFile';
import { InputText } from 'src/components/InputText';
import { NameFiles } from 'src/features/admin/components/Categories';
import { UseFormReturn } from 'src/hooks';
import { FormInputStore } from '../types';

interface StoreProfileManagerUIProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  formNameFiles?: NameFiles;
  onSubmitRegister: (e: FormEvent) => void;
  onChangeFormFileInput: (
    inputName: string
  ) => (file: File | undefined) => void;
  selectedStoreImage?: string;
  selectedCoverImage?: string;
  isEditingStoreImage: boolean;
  isEditingCoverImage: boolean;
  onToggleStoreEdit: () => void;
  onToggleCoverEdit: () => void;
  isStore: boolean;
}

export const StoreProfileManagerUI: React.FC<StoreProfileManagerUIProps> = ({
  formData,
  formErrors,
  selectedStoreImage,
  formNameFiles,
  selectedCoverImage,
  isEditingStoreImage,
  isEditingCoverImage,
  isStore,
  onSubmitRegister,
  onChangeFormInput,
  onChangeFormFileInput,
  onToggleStoreEdit,
  onToggleCoverEdit,
}) => {
  const imageRefBanner = React.useRef<HTMLInputElement>(null);
  const imageRefDetail = React.useRef<HTMLInputElement>(null);

  return (
    <div className="min-h-screen w-full bg-gradient from-red-50 to-red-100 md:p-4 md:p-8 overflow-x-hidden">
      <div className="w-full md:max-w-6xl md:mx-auto">
        <div className="bg-white/90 backdrop-blur-sm md:rounded-3xl md:shadow-2xl md:border border-white/20 overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 via-red-600 to-red-600 p-6 md:p-8 text-white">
            <h1 className="text-2xl md:text-4xl font-bold mb-2">
              {isStore ? 'Gerenciar Loja' : 'Criar Nova Loja'}
            </h1>
            <p className="text-red-100 text-base md:text-lg opacity-90">
              {isStore
                ? 'Gerenciar informações da loja'
                : 'Você ainda não criou uma loja'}
            </p>
          </div>

          <form onSubmit={onSubmitRegister} className="p-2 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-gray-50 to-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                    <div className="w-2 h-8 bg-gradient-to-b from-red-500 to-red-500 rounded-full mr-3"></div>
                    Informações da Loja
                  </h3>

                  <div className="space-y-6">
                    <InputText
                      label="Nome Loja"
                      name="storeName"
                      value={formData[FormInputStore.name]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputStore.name)(e.target.value)
                      }
                      error={formErrors.name && formErrors.name[0]}
                      inputClassName="rounded-xl p-4 w-full bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-red-400 focus:ring-4 focus:ring-red-100 transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Digite o nome da loja"
                    />

                    <InputText
                      label="E-mail"
                      name="storeEmail"
                      value={formData[FormInputStore.email]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputStore.email)(e.target.value)
                      }
                      error={formErrors.email && formErrors.email[0]}
                      inputClassName="rounded-xl p-4 w-full bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-red-400 focus:ring-4 focus:ring-red-100 transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Digite o e-mail"
                    />

                    <InputText
                      name="storePhone"
                      label="Telefone"
                      value={formData[FormInputStore.phone]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputStore.phone)(e.target.value)
                      }
                      error={formErrors.phone && formErrors.phone[0]}
                      inputClassName="rounded-xl p-4 w-full bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-red-400 focus:ring-4 focus:ring-red-100 transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Digite o telefone"
                    />

                    <InputText
                      label="Descrição breve"
                      name="storeDescription"
                      value={formData[FormInputStore.description]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputStore.description)(
                          e.target.value
                        )
                      }
                      error={
                        formErrors.description && formErrors.description[0]
                      }
                      inputClassName="rounded-xl p-4 w-full bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-red-400 focus:ring-4 focus:ring-red-100 transition-all duration-300 shadow-sm hover:shadow-md"
                      placeholder="Descrição da loja"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="bg-gradient-to-r from-gray-50 to-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                    <div className="w-2 h-8 bg-gradient-to-b from-red-500 to-red-500 rounded-full mr-3"></div>
                    Imagens da Loja
                  </h3>

                  <div className="mb-10">
                    <label className="block text-gray-700 font-medium mb-4 text-center">
                      Foto do Perfil
                    </label>
                    <div className="flex justify-center">
                      <div className="relative w-48 h-48 group">
                        {isEditingStoreImage ? (
                          <div className="w-full h-full bg-white rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center p-4 space-y-4">
                            <InputFile
                              refInput={imageRefDetail}
                              name="imageDetail"
                              fileName={formNameFiles?.imageDetail}
                              onChange={(e) =>
                                onChangeFormFileInput(
                                  FormInputStore.imageDetail
                                )(e.target.files?.[0])
                              }
                              error={
                                formErrors.imageDetail &&
                                formErrors.imageDetail[0]
                              }
                              wrapperClass="file:bg-gradient-to-r file:from-red-500 file:to-red-500 file:text-white file:border-0 file:rounded-lg file:px-6 file:py-2 file:font-medium file:cursor-pointer file:hover:from-red-600 file:hover:to-red-600 file:transition-all file:duration-300"
                            />
                            <button
                              type="button"
                              onClick={onToggleStoreEdit}
                              className="bg-gradient-to-r from-red-500 to-red-500 text-white px-6 py-2 rounded-lg hover:from-red-600 hover:to-red-600 transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="w-full h-full rounded-2xl overflow-hidden shadow-2xl border-4 border-white group-hover:shadow-3xl transition-all duration-300">
                              {selectedStoreImage ? (
                                <img
                                  src={
                                    selectedStoreImage.startsWith('data:image/')
                                      ? selectedStoreImage
                                      : `${process.env.REACT_APP_BASE_URL}/${selectedStoreImage}`
                                  }
                                  alt="Selected Profile"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                  <div className="text-center">
                                    <div className="w-12 h-12 bg-gray-300 rounded-full mx-auto mb-2"></div>
                                    <span className="text-gray-500 text-sm">
                                      Nenhuma imagem
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={onToggleStoreEdit}
                              className="absolute -bottom-2 -right-2 bg-gradient-to-r from-red-500 to-red-500 text-white p-3 rounded-full hover:from-red-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 group-hover:scale-110"
                              title="Alterar imagem"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Tamanho recomendado: <strong>48x48px</strong>
                    </p>
                    <p className="text-xs text-gray-500 text-center mt-4 bg-gray-50 rounded-lg p-2">
                      Tamanho máximo: 1 MB. Formatos: JPEG, PNG.
                    </p>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-4 text-center">
                      Imagem da Capa
                    </label>
                    <div className="relative w-full h-40 group">
                      {isEditingCoverImage ? (
                        <div className="w-full h-full bg-white rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center p-4 space-y-4">
                          <InputFile
                            refInput={imageRefBanner}
                            name="imageBanner"
                            fileName={formNameFiles?.imageBanner}
                            onChange={(e) =>
                              onChangeFormFileInput(FormInputStore.imageBanner)(
                                e.target.files?.[0]
                              )
                            }
                            error={
                              formErrors.imageBanner &&
                              formErrors.imageBanner[0]
                            }
                            wrapperClass="file:bg-gradient-to-r file:from-red-500 file:to-red-500 file:text-white file:border-0 file:rounded-lg file:px-6 file:py-2 file:font-medium file:cursor-pointer file:hover:from-red-600 file:hover:to-red-600 file:transition-all file:duration-300"
                          />
                          <button
                            type="button"
                            onClick={onToggleCoverEdit}
                            className="bg-gradient-to-r from-red-500 to-red-500 text-white px-6 py-2 rounded-lg hover:from-red-600 hover:to-red-600 transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="w-full h-full rounded-2xl overflow-hidden shadow-2xl border-4 border-white group-hover:shadow-3xl transition-all duration-300">
                            {selectedCoverImage ? (
                              <img
                                src={
                                  selectedCoverImage.startsWith('data:image/')
                                    ? selectedCoverImage
                                    : `${process.env.REACT_APP_BASE_URL}/${selectedCoverImage}`
                                }
                                alt="Selected Cover"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="w-16 h-8 bg-gray-300 rounded mx-auto mb-2"></div>
                                  <span className="text-gray-500 text-sm">
                                    Nenhuma imagem
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={onToggleCoverEdit}
                            className="absolute -bottom-2 -right-2 bg-gradient-to-r from-red-500 to-red-500 text-white p-3 rounded-full hover:from-red-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 group-hover:scale-110"
                            title="Alterar imagem da capa"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Tamanho recomendado: <strong>1845x112px</strong>
                    </p>
                    <p className="text-xs text-gray-500 text-center mt-4 bg-gray-50 rounded-lg p-2">
                      Tamanho máximo: 2 MB. Formatos: JPEG, PNG.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 flex justify-center">
              <div className="p-6">
                <Button
                  type="submit"
                  title={isStore ? 'Salvar Alterações' : 'Criar minha loja'}
                  buttonStyle="red"
                  size="md"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
